import { useEffect, useMemo, useState } from "react";
import { teamActions } from "redux/Ldb/actions/teamActions";
import { useAppDispatch, useAppSelector } from "../storeHooks";
import { TeamMember, TeamFilters } from "redux/Ldb/types/teamTypes";
import { useInfiniteScrollV2 } from "hooks/useInfiniteScrollV2";
import useGetUserProfile from "hooks/useGetUserProfile";
import { getFromStorage } from "helpers";

const generateFilterKey = (filters?: TeamFilters): string => {
    if (!filters) return "default";

    // Create a stable string representation of the filters
    const filterEntries = Object.entries(filters)
        .filter(([_, value]) => value !== undefined && value !== null && value !== "")
        .sort(([a], [b]) => a.localeCompare(b));

    return filterEntries.length > 0
        ? filterEntries.map(([key, value]) => `${key}:${value}`).join("|")
        : "default";
};

interface UseGetTeamMembers2Return {
    isSuccess: boolean;
    isError: boolean;
    isLoading: boolean;
    isFetching: boolean;
    data: TeamMember[];
    error: any;
    pagination: { currentPage: string; noOfPages: string };
    refetch: () => void;
}

const useGetTeamMembers2 = (
    filters?: TeamFilters,
    options: {
        skip?: boolean;
        isInfiniteScroll?: boolean;
        scrollContentId?: string;
        pollingInterval?: number; //milliseconds
        refetchOnMount?: boolean;
    } = {}
): UseGetTeamMembers2Return => {
    const {
        skip = false,
        isInfiniteScroll = false,
        pollingInterval = 0,
        scrollContentId,
        refetchOnMount = false,
    } = options;
    const dispatch = useAppDispatch();

    const [profile] = useGetUserProfile();
    const userProfile = getFromStorage("ally-user");

    const filterKey = generateFilterKey(filters);

    const [isLoading, setIsLoading] = useState<Map<string, boolean>>(new Map([[filterKey, false]]));
    const [isFetching, setIsFetching] = useState<Map<string, boolean>>(
        new Map([[filterKey, false]])
    );
    const [isSuccess, setIsSuccess] = useState<Map<string, any>>(new Map([[filterKey, null]]));
    const [loadingIndex, setLoadingIndex] = useState<Map<string, number>>(
        new Map([[filterKey, 0]])
    );

    const { fetchingTeamMembers2, fetchedTeamMembers2Success, fetchedTeamMembers2Failure } =
        useAppSelector((state) => state.team);

    const { addTeamMemberSuccess } = useAppSelector((state) => state.auth);

    const [teamMembersList, setTeamMembersList] = useState<Map<string, { [key: string]: any }[]>>(
        new Map([[filterKey, []]])
    );
    const [pagination, setPagination] = useState<
        Map<string, { currentPage: string; noOfPages: string }>
    >(new Map([[filterKey, { currentPage: "1", noOfPages: "" }]]));

    useInfiniteScrollV2(
        useMemo(
            () => ({
                containerId: scrollContentId,
                onLoadMore: () => {
                    if (isInfiniteScroll) {
                        setPagination((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(filterKey, {
                                ...prev.get(filterKey),
                                currentPage: String(Number(prev.get(filterKey)?.currentPage) + 1),
                            });
                            return newMap;
                        });
                    }
                },
                loading: isFetching.get(filterKey) || false,
                hasMore:
                    isSuccess.get(filterKey)?.data?.pagination?.current <
                    isSuccess.get(filterKey)?.data?.pagination?.number_of_pages,
                enabled: isInfiniteScroll,
                offset: 50,
            }),
            [
                scrollContentId,
                isFetching.get(filterKey),
                filterKey,
                isSuccess.get(filterKey),
                isInfiniteScroll,
            ]
        )
    );

    const handleFetchTeamMembers = (params: TeamFilters) => {
        loadingIndex.get(filterKey) === 0 &&
            setIsLoading((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, true);
                return newMap;
            });

        setIsFetching((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, true);
            return newMap;
        });

        dispatch(
            teamActions.fetchTeamMembers2(
                params,
                profile?.isMultiBrandAccount || profile?.accountType === "brand"
                    ? userProfile?.mainToken || userProfile?.token
                    : profile?.token
            )
        );
    };

    // Reset list and pagination when filters change
    useEffect(() => {
        if (!skip) {
            setTeamMembersList(new Map([[filterKey, []]]));
            setPagination(new Map([[filterKey, { currentPage: "1", noOfPages: "" }]]));
            setIsFetching(new Map([[filterKey, false]]));
        }
    }, [filters, skip]);

    useEffect(() => {
        if (!skip) {
            const params = {
                limit: Number(pagination.get(filterKey)?.noOfPages),
                ...filters,
                page: Number(pagination.get(filterKey)?.currentPage),
            };
            handleFetchTeamMembers(params);
        }
    }, [filters, skip, pagination]);

    useEffect(() => {
        if (Boolean(fetchedTeamMembers2Success)) {
            setTeamMembersList((prev) => {
                const newMap = new Map(prev);
                const newTeamMembers = fetchedTeamMembers2Success?.data?.teamMembers || [];
                const updatedList = [...(prev.get(filterKey) || [])];

                // If polling interval is set, return the new team members
                if (!!pollingInterval) {
                    newMap.set(filterKey, newTeamMembers);
                    return newMap;
                }

                newTeamMembers.forEach((newTeamMember) => {
                    const existingIndex = updatedList.findIndex(
                        (existing) => existing._id === newTeamMember._id
                    );

                    if (existingIndex !== -1) {
                        // Update existing item
                        updatedList[existingIndex] = newTeamMember;
                    } else {
                        // Add new item
                        updatedList.push(newTeamMember);
                    }
                });

                newMap.set(filterKey, updatedList);

                return newMap;
            });
        }
    }, [fetchedTeamMembers2Success]);

    const refetch = () => {
        const params = {
            ...filters,
            // page: 1,
            // limit: Number(pagination.get(filterKey)?.noOfPages),
        };
        handleFetchTeamMembers(params);
    };

    useEffect(() => {
        if (Boolean(addTeamMemberSuccess)) {
            refetch();
        }
    }, [addTeamMemberSuccess]);

    useEffect(() => {
        if (!fetchingTeamMembers2 && !!fetchedTeamMembers2Success) {
            loadingIndex.get(filterKey) === 1 &&
                setIsLoading((prev) => {
                    const newMap = new Map(prev);
                    newMap.set(filterKey, false);
                    return newMap;
                });
            setLoadingIndex((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, prev.get(filterKey) + 1);
                return newMap;
            });
        }
    }, [fetchingTeamMembers2, fetchedTeamMembers2Success]);

    useEffect(() => {
        if (!skip && pollingInterval) {
            const interval = setInterval(() => {
                refetch();
            }, pollingInterval);
            return () => clearInterval(interval);
        }
    }, [pollingInterval, skip]);

    useEffect(() => {
        setIsFetching((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, fetchingTeamMembers2);
            return newMap;
        });
    }, [fetchingTeamMembers2]);

    useEffect(() => {
        if (fetchedTeamMembers2Failure) {
            setIsFetching((prev) => {
                const newMap = new Map(prev);
                newMap.set(filterKey, false);
                return newMap;
            });
        }
    }, [fetchedTeamMembers2Failure]);

    useEffect(() => {
        setIsSuccess((prev) => {
            const newMap = new Map(prev);
            newMap.set(filterKey, fetchedTeamMembers2Success);
            return newMap;
        });
    }, [fetchedTeamMembers2Success]);

    return {
        isSuccess: Boolean(fetchedTeamMembers2Success),
        isError: Boolean(fetchedTeamMembers2Failure),
        isLoading: isLoading.get(filterKey) || false,
        isFetching: isFetching.get(filterKey) || false,
        data: refetchOnMount
            ? fetchedTeamMembers2Success?.data?.teamMembers || []
            : teamMembersList.get(filterKey) || [],
        error: fetchedTeamMembers2Failure,
        pagination: pagination.get(filterKey) || { currentPage: "1", noOfPages: "" },
        refetch,
    };
};

export default useGetTeamMembers2;
